import { DateTime } from 'luxon'

// Pinned so day and month names don't follow the browser's language.
const at = (iso: string): DateTime => DateTime.fromISO(iso, { locale: 'en-US' })

const isoDate = (date: DateTime): string => date.toFormat('yyyy-MM-dd')

export type WeekDay = { iso: string; weekday: number; label: string; dayOfMonth: number }

// Built on luxon's `.weekday`, not `startOf('week')`, which follows the runtime's locale.
export const weekDaysFor = (referenceIso: string, weekOffset: number): WeekDay[] => {
  const reference = at(referenceIso)
  const start = reference.minus({ days: reference.weekday - 1 }).plus({ weeks: weekOffset })

  return Array.from({ length: 7 }, (_, index) => {
    const date = start.plus({ days: index })

    return { iso: isoDate(date), weekday: index, label: date.toFormat('EEE'), dayOfMonth: date.day }
  })
}

export const weekRangeLabel = (days: WeekDay[]): string => {
  const start = at(days[0].iso)
  const end = at(days[6].iso)

  const startLabel = start.month === end.month ? start.toFormat('d') : start.toFormat('d LLL')

  return `${startLabel}–${end.toFormat('d LLL yyyy')}`
}

export const dayFor = (referenceIso: string, dayOffset: number): WeekDay => {
  const date = at(referenceIso).plus({ days: dayOffset })

  return {
    iso: isoDate(date),
    weekday: date.weekday - 1,
    label: date.toFormat('EEE'),
    dayOfMonth: date.day,
  }
}

export const dayLabel = (day: WeekDay): string => at(day.iso).toFormat('EEEE, d LLL yyyy')

export type MonthDay = { iso: string; dayOfMonth: number; weekday: number; inCurrentMonth: boolean }

export const monthGridFor = (referenceIso: string, monthOffset: number): MonthDay[] => {
  const monthStart = at(referenceIso).startOf('month').plus({ months: monthOffset })
  const monthEnd = monthStart.endOf('month')
  const gridStart = monthStart.minus({ days: monthStart.weekday - 1 })
  const gridEnd = monthEnd.plus({ days: 7 - monthEnd.weekday })

  const days: MonthDay[] = []
  for (let date = gridStart; date <= gridEnd; date = date.plus({ days: 1 })) {
    days.push({
      iso: isoDate(date),
      dayOfMonth: date.day,
      weekday: date.weekday - 1,
      inCurrentMonth: date.month === monthStart.month && date.year === monthStart.year,
    })
  }

  return days
}

export const monthLabel = (referenceIso: string, monthOffset: number): string =>
  at(referenceIso).startOf('month').plus({ months: monthOffset }).toFormat('LLLL yyyy')
